import type { ReactNode } from "react";

/**
 * "Before you paste anything into an AI tool" — an OPTIONAL, illustrated
 * awareness slide offered as the last beat of the AI Assistant's up-front
 * tutorial (AiAssistant.tsx, introStep 3). Purely a read-and-close overlay:
 * it never blocks or changes the task, and closing it (either button) drops
 * the learner straight into the game, same as skipping it outright.
 *
 * Layout constraint: this overlay is rendered INSIDE `.winapp.cgpt-browser`,
 * which is overflow:hidden and only ~62vh (the 3D monitor). The card must
 * fill that overlay and keep Close / Continue in a sticky footer — never
 * size against 88vh, which is taller than the monitor on 1440x900+.
 */
export function AiSecurityBriefing({ onClose }: { onClose: () => void }) {
  return (
    <div className="aisec-wrap" role="dialog" aria-label="Before you paste anything into an AI tool">
      <div className="aisec-card">
        <div className="aisec-head">
          <div className="bue-kicker">AI SECURITY BASICS</div>
          <h2 className="aisec-title">Before you paste anything into an AI tool</h2>
          <button type="button" className="aisec-close" onClick={onClose} aria-label="Close">
            {"\u2715"}
          </button>
        </div>
        <div className="aisec-body">
          <p className="bue-lede">
            AI assistants are genuinely useful {"\u2014"} but the moment company data goes into one, you've
            handed over some control of where it goes next. Three things are worth two minutes first.
          </p>

          <div className="aisec-sections">
            <AiSecSection
              index={0}
              tone="expose"
              icon={<IconDataOut />}
              title="Your data can leave the building"
              body={
                "A personal or free-tier AI account may store your prompts and files, use them to improve its models, or keep them indefinitely on servers you don't control \u2014 sometimes in a different country entirely. A corporate AI workspace usually comes with contractual guarantees a personal login doesn't: no training on your data, a defined retention period, and data residency you can actually point to."
              }
            />
            <AiSecSection
              index={1}
              tone="link"
              icon={<IconShareLink />}
              title="A share link isn't &ldquo;just a link&rdquo;"
              body={
                "A public share link doesn't send one message \u2014 it opens the WHOLE conversation, including every file you attached, to anyone who has that URL. Links get forwarded, pasted into other chats, and sometimes indexed by search engines. If a result has to leave the AI tool, send it through a channel your company controls, not a public link."
              }
            />
            <AiSecSection
              index={2}
              tone="legal"
              icon={<IconLegalShield />}
              title="The law may have already decided for you"
              body={
                "Client contracts, NDAs, and data-protection rules like GDPR often explicitly restrict where confidential or personal data is allowed to go \u2014 including into a third-party AI service. Sending regulated data to an unapproved AI tool can be a reportable data-protection incident, even if nothing else goes wrong with it afterward."
              }
            />
          </div>

          <ul className="bue-facts">
            <li>
              Check which account you're signed into before attaching anything {"\u2014"} personal and corporate
              AI accounts are not the same product.
            </li>
            <li>
              A &ldquo;share&rdquo; link is the whole conversation, not a private message {"\u2014"} treat it like a
              public webpage.
            </li>
            <li>
              When in doubt: would you be allowed to email this file to an external address? If not, don't paste it
              into an AI tool either.
            </li>
          </ul>
        </div>
        <div className="aisec-foot">
          <button type="button" className="aisec-foot-close" onClick={onClose}>
            Close
          </button>
          <button type="button" className="btn busb-info-got-it aisec-foot-continue" onClick={onClose}>
            Got it {"\u2014"} start the task
          </button>
        </div>
      </div>
    </div>
  );
}

function AiSecSection({
  index,
  tone,
  icon,
  title,
  body,
}: {
  index: number;
  tone: "expose" | "link" | "legal";
  icon: ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className={`aisec-sec aisec-sec--${tone}`} style={{ animationDelay: `${index * 140}ms` }}>
      <span className={`aisec-ico aisec-ico--${tone}`} aria-hidden>
        {icon}
      </span>
      <div className="aisec-sec-main">
        <h3 dangerouslySetInnerHTML={{ __html: title }} />
        <p>{body}</p>
      </div>
    </div>
  );
}

/** A document with data curling up and away into a cloud — "your data can leave the building". */
function IconDataOut() {
  return (
    <svg viewBox="0 0 40 40" fill="none" focusable="false">
      <rect x="7" y="9" width="15" height="20" rx="1.6" stroke="currentColor" strokeWidth="2" />
      <path d="M10.5 14h8M10.5 18h8M10.5 22h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path
        d="M23 15c2-2.5 5.5-2 6.6 1 2.6.2 3.9 3.8 1.4 5.4-1 .6-8.6.6-9.6 0-2.3-1.4-1.4-4.8 1.6-5.2Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="M24.5 12.5c1.2-1.6 3.6-1 3.9 1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" opacity=".7" />
    </svg>
  );
}

/** A chain link fanning out to several small dots — "a share link isn't just a link". */
function IconShareLink() {
  return (
    <svg viewBox="0 0 40 40" fill="none" focusable="false">
      <rect x="12" y="16" width="16" height="8" rx="4" stroke="currentColor" strokeWidth="2" transform="rotate(-35 20 20)" />
      <path d="M16.5 23.5 23.5 16.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="32" cy="9" r="2" fill="currentColor" />
      <circle cx="35" cy="20" r="2" fill="currentColor" />
      <circle cx="30" cy="31" r="2" fill="currentColor" />
      <path d="M25 14 31 10M27 20h6M25 26 29 30" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" opacity=".55" />
    </svg>
  );
}

/** A shield with a small scale/checkmark motif — "the law may have decided for you". */
function IconLegalShield() {
  return (
    <svg viewBox="0 0 40 40" fill="none" focusable="false">
      <path
        d="M20 6.5 30 10v9c0 8-6 12.5-10 14.5-4-2-10-6.5-10-14.5v-9Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d="M14.5 20.5 18 24l7.5-8" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
